/**
 * Pläne, Trainingstage und ihre Übungen — der Editor darunter.
 *
 * Ein Plan („Hybrid: 3× Kraft + 2× Laufen") hat Trainingstage („Kraft A"), und
 * ein Trainingstag hat Übungen an einer Position. Sätze und Spanne stehen am
 * Plan-Eintrag, nicht nur an der Übung: „heute 4×5 statt 3×8" darf einen
 * anderen Plan nicht mitverändern.
 */

import { STARTER_PLANS, newId } from '@kraftwerk/shared';
import { db } from './local.js';
import type {
  KraftwerkDatabase,
  LocalExercise,
  LocalPlan,
  LocalPlanDay,
  LocalPlanExercise,
} from './local.js';
import { stamp, writeRow, writeRows } from './write.js';

// --- Pläne -------------------------------------------------------------------

export async function listPlans(database: KraftwerkDatabase = db): Promise<LocalPlan[]> {
  const rows = await database.training_plans.toArray();
  return rows
    .filter((row) => !row.deletedAt)
    .sort(
      (a, b) =>
        Number(b.active) - Number(a.active) ||
        a.sortOrder - b.sortOrder ||
        a.name.localeCompare(b.name, 'de'),
    );
}

/**
 * Wie viele Plan-Vorlagen das Konto noch nicht kennt — etwa Calisthenics 3×,
 * das nach dem Anlegen dazukam. Gelöschte Vorlagen zählen als bekannt (der
 * Server legt sie auch nicht neu an). Ohne eine einzige Vorlage auf dem Gerät:
 * vor dem ersten Abgleich 0 (sonst sähe jedes frische Gerät „neue Vorlagen“),
 * danach alle — ein Konto ohne Startbestand, etwa ein Kind, das Training
 * freigegeben bekam, kam sonst nie an die Vorlagen (Geräteabnahme 28.10.2026).
 */
export async function missingStarterPlans(database: KraftwerkDatabase = db): Promise<number> {
  const keys = new Set(
    (await database.training_plans.toArray()).map((row) => row.catalogKey).filter(Boolean),
  );
  if (keys.size === 0) {
    const synced = await database.meta.get('cursor:training_plans');
    return synced ? STARTER_PLANS.length : 0;
  }
  return STARTER_PLANS.filter((plan) => !keys.has(plan.key)).length;
}

export async function activePlan(database: KraftwerkDatabase = db): Promise<LocalPlan | null> {
  const rows = await listPlans(database);
  return rows.find((row) => row.active) ?? null;
}

export async function createPlan(
  name: string,
  note: string | null = null,
  database: KraftwerkDatabase = db,
): Promise<LocalPlan> {
  const now = stamp();
  const row: LocalPlan = {
    id: newId(),
    updatedAt: now,
    deletedAt: null,
    catalogKey: null,
    name: name.trim(),
    note,
    active: false,
    startedAt: null,
    sortOrder: 0,
  };
  return writeRow('training_plans', row, 'upsert', database);
}

export async function updatePlan(
  id: string,
  changes: Partial<Pick<LocalPlan, 'name' | 'note' | 'sortOrder'>>,
  database: KraftwerkDatabase = db,
): Promise<LocalPlan | null> {
  const existing = await database.training_plans.get(id);
  if (!existing || existing.deletedAt) return null;
  return writeRow(
    'training_plans',
    { ...existing, ...changes, updatedAt: stamp() },
    'upsert',
    database,
  );
}

/**
 * Diesen Plan zum laufenden machen — und alle anderen abwählen.
 *
 * In einer Transaktion, weil zwei aktive Pläne die Oberfläche vor eine Frage
 * stellen würden, die sie nicht beantworten kann.
 */
export async function activatePlan(id: string, database: KraftwerkDatabase = db): Promise<void> {
  const now = stamp();
  const all = await listPlans(database);
  const changes = all
    .filter((plan) => plan.active !== (plan.id === id))
    .map((plan) => ({
      table: 'training_plans' as const,
      row: {
        ...plan,
        active: plan.id === id,
        // Bezugspunkt für die wöchentliche Rotation der Zusatzübungen.
        startedAt: plan.id === id ? (plan.startedAt ?? now) : plan.startedAt,
        updatedAt: now,
      },
    }));

  await writeRows(changes, database);
}

/** Plan weich löschen — samt Tagen und deren Einträgen. */
export async function deletePlan(id: string, database: KraftwerkDatabase = db): Promise<void> {
  const plan = await database.training_plans.get(id);
  if (!plan || plan.deletedAt) return;

  const now = stamp();
  const days = (await database.plan_days.where('planId').equals(id).toArray()).filter(
    (row) => !row.deletedAt,
  );
  const dayIds = days.map((day) => day.id);
  const entries = (await database.plan_exercises.toArray()).filter(
    (row) => !row.deletedAt && dayIds.includes(row.planDayId),
  );

  await writeRows(
    [
      { table: 'training_plans', row: { ...plan, deletedAt: now, updatedAt: now }, kind: 'delete' },
      ...days.map((day) => ({
        table: 'plan_days' as const,
        row: { ...day, deletedAt: now, updatedAt: now },
        kind: 'delete' as const,
      })),
      ...entries.map((entry) => ({
        table: 'plan_exercises' as const,
        row: { ...entry, deletedAt: now, updatedAt: now },
        kind: 'delete' as const,
      })),
    ],
    database,
  );
}

// --- Trainingstage -----------------------------------------------------------

export async function listDays(
  planId: string,
  database: KraftwerkDatabase = db,
): Promise<LocalPlanDay[]> {
  const rows = await database.plan_days.where('planId').equals(planId).toArray();
  return rows.filter((row) => !row.deletedAt).sort((a, b) => a.position - b.position);
}

export async function addDay(
  planId: string,
  name: string,
  database: KraftwerkDatabase = db,
): Promise<LocalPlanDay> {
  const existing = await listDays(planId, database);
  const now = stamp();
  const row: LocalPlanDay = {
    id: newId(),
    updatedAt: now,
    deletedAt: null,
    planId,
    catalogKey: null,
    name: name.trim(),
    position: existing.length,
  };
  return writeRow('plan_days', row, 'upsert', database);
}

export async function renameDay(
  id: string,
  name: string,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const existing = await database.plan_days.get(id);
  if (!existing || existing.deletedAt) return;
  await writeRow(
    'plan_days',
    { ...existing, name: name.trim(), updatedAt: stamp() },
    'upsert',
    database,
  );
}

export async function deleteDay(id: string, database: KraftwerkDatabase = db): Promise<void> {
  const day = await database.plan_days.get(id);
  if (!day || day.deletedAt) return;

  const now = stamp();
  const entries = (await database.plan_exercises.where('planDayId').equals(id).toArray()).filter(
    (row) => !row.deletedAt,
  );

  await writeRows(
    [
      { table: 'plan_days', row: { ...day, deletedAt: now, updatedAt: now }, kind: 'delete' },
      ...entries.map((entry) => ({
        table: 'plan_exercises' as const,
        row: { ...entry, deletedAt: now, updatedAt: now },
        kind: 'delete' as const,
      })),
    ],
    database,
  );
}

// --- Übungen im Trainingstag -------------------------------------------------

export async function listDayExercises(
  planDayId: string,
  database: KraftwerkDatabase = db,
): Promise<LocalPlanExercise[]> {
  const rows = await database.plan_exercises.where('planDayId').equals(planDayId).toArray();
  return rows.filter((row) => !row.deletedAt).sort((a, b) => a.position - b.position);
}

export async function addExerciseToDay(
  planDayId: string,
  exercise: LocalExercise,
  database: KraftwerkDatabase = db,
): Promise<LocalPlanExercise> {
  const existing = await listDayExercises(planDayId, database);
  const now = stamp();
  const row: LocalPlanExercise = {
    id: newId(),
    updatedAt: now,
    deletedAt: null,
    planDayId,
    exerciseId: exercise.id,
    position: existing.length,
    // Vorbelegt aus der Übung; im Plan änderbar, ohne die Übung anzufassen.
    sets: exercise.defaultSets,
    repMin: exercise.repMin,
    repMax: exercise.repMax,
    restSeconds: null,
    note: null,
    supersetWithPrevious: false,
  };
  return writeRow('plan_exercises', row, 'upsert', database);
}

export async function updatePlanExercise(
  id: string,
  changes: Partial<
    Pick<
      LocalPlanExercise,
      'sets' | 'repMin' | 'repMax' | 'restSeconds' | 'note' | 'supersetWithPrevious'
    >
  >,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const existing = await database.plan_exercises.get(id);
  if (!existing || existing.deletedAt) return;
  await writeRow(
    'plan_exercises',
    { ...existing, ...changes, updatedAt: stamp() },
    'upsert',
    database,
  );
}

/** Eine Übung im Trainingstag gegen eine andere tauschen — Position bleibt. */
export async function swapPlanExercise(
  id: string,
  exerciseId: string,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const existing = await database.plan_exercises.get(id);
  if (!existing || existing.deletedAt) return;
  await writeRow(
    'plan_exercises',
    { ...existing, exerciseId, updatedAt: stamp() },
    'upsert',
    database,
  );
}

export async function removePlanExercise(
  id: string,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const existing = await database.plan_exercises.get(id);
  if (!existing || existing.deletedAt) return;
  const now = stamp();
  await writeRow(
    'plan_exercises',
    { ...existing, deletedAt: now, updatedAt: now },
    'delete',
    database,
  );
}

/**
 * Eintrag um einen Platz verschieben.
 *
 * Es werden beide betroffenen Zeilen geschrieben — die Position ist eine
 * Eigenschaft der Zeile, nicht der Liste. Nur eine zu schreiben ergäbe zwei
 * Einträge mit derselben Nummer.
 */
export async function movePlanExercise(
  id: string,
  direction: -1 | 1,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const existing = await database.plan_exercises.get(id);
  if (!existing || existing.deletedAt) return;

  const siblings = await listDayExercises(existing.planDayId, database);
  const index = siblings.findIndex((entry) => entry.id === id);
  const targetIndex = index + direction;
  const other = siblings[targetIndex];
  if (index === -1 || !other) return;

  const now = stamp();
  await writeRows(
    [
      {
        table: 'plan_exercises',
        row: { ...existing, position: other.position, updatedAt: now },
      },
      {
        table: 'plan_exercises',
        row: { ...other, position: existing.position, updatedAt: now },
      },
    ],
    database,
  );
}
